import { useCallback, useEffect, useMemo, useState } from "react";
import api, { getErrorMessage, downloadFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { FolderTree, FileDown, Lock } from "lucide-react";

const STATUS_STYLE = {
  COMPLETED: "bg-green-100 text-green-800 border-green-300",
  LOCKED: "bg-green-100 text-green-800 border-green-300",
  FINAL: "bg-green-100 text-green-800 border-green-300",
  READY_TO_FINALIZE: "bg-green-100 text-green-800 border-green-300",
  READY_FOR_CONCLUSION: "bg-amber-100 text-amber-800 border-amber-300",
  READY_FOR_REVIEW: "bg-amber-100 text-amber-800 border-amber-300",
  REVIEW_IN_PROGRESS: "bg-amber-100 text-amber-800 border-amber-300",
  REVIEW_NOTES_OPEN: "bg-amber-100 text-amber-800 border-amber-300",
  PENDING_EVIDENCE: "bg-amber-100 text-amber-800 border-amber-300",
  PENDING_PREPARER: "bg-amber-100 text-amber-800 border-amber-300",
  IN_PROGRESS: "bg-blue-100 text-blue-800 border-blue-300",
  IN_REVIEW: "bg-blue-100 text-blue-800 border-blue-300",
  NOT_APPLICABLE: "bg-slate-100 text-slate-700 border-slate-300",
  NOT_STARTED: "bg-slate-100 text-slate-700 border-slate-300",
  DRAFT: "bg-slate-100 text-slate-700 border-slate-300",
  SUPERSEDED: "bg-slate-100 text-slate-700 border-slate-300",
  UNLINKED: "bg-slate-100 text-slate-700 border-slate-300",
  NONE: "bg-slate-100 text-slate-700 border-slate-300",
  PENDING: "bg-amber-100 text-amber-800 border-amber-300",
  LINKED: "bg-green-100 text-green-800 border-green-300",
  BLOCKED: "bg-red-100 text-red-800 border-red-300",
  STALE: "bg-red-100 text-red-800 border-red-300",
  MATERIALITY_NOT_SET: "bg-amber-100 text-amber-800 border-amber-300",
  MATERIALITY_BOUND: "bg-green-100 text-green-800 border-green-300",
};

const LABELS = {
  NOT_STARTED: "Belum dimulai", IN_PROGRESS: "Dikerjakan",
  PENDING_EVIDENCE: "Menunggu bukti", PENDING_PREPARER: "Menunggu penyusun",
  READY_FOR_REVIEW: "Siap direviu", REVIEW_IN_PROGRESS: "Sedang direviu",
  REVIEW_NOTES_OPEN: "Catatan reviu terbuka", READY_FOR_CONCLUSION: "Siap disimpulkan",
  COMPLETED: "Selesai", NOT_APPLICABLE: "Tidak berlaku", BLOCKED: "Tertahan",
  STALE: "Usang", SUPERSEDED: "Digantikan", LOCKED: "Terkunci",
  DRAFT: "Draf", IN_REVIEW: "Dalam reviu", READY_TO_FINALIZE: "Siap difinalisasi",
  FINAL: "Final", LINKED: "Tertaut", UNLINKED: "Belum tertaut", PENDING: "Belum disimpulkan",
  NONE: "Tidak ada", CURRENT: "Kini",
  MATERIALITY_BOUND: "Terikat materialitas perikatan",
  MATERIALITY_NOT_SET: "Materialitas belum ditetapkan",
  DISCLOSED: "Dinyatakan sebagai keterbatasan",
};

const GATE_LABELS = {
  PLANNING_COMPLETE: "Perencanaan selesai",
  MATERIALITY_RESOLVED: "Materialitas ditetapkan",
  SIGNIFICANT_RISKS_ANSWERED: "Risiko signifikan direspons",
  RISK_ASSESSMENT_COMPLETE: "Penilaian risiko selesai",
  CYCLE_CONCLUSIONS: "Simpulan siklus lengkap",
  SAMPLING_APPROVED: "Rencana sampling disetujui",
  SAMPLES_TESTED: "Sampel telah diuji",
  EXCEPTIONS_RESOLVED: "Pengecualian diselesaikan",
  MISSTATEMENTS_DISPOSED: "Kesalahan penyajian didisposisi",
  REVIEW_NOTES_CLOSED: "Catatan reviu ditutup",
  COMPLETION_CHECKLIST: "Penyelesaian lengkap",
  MANAGEMENT_REPRESENTATION: "Surat representasi tersedia",
  EQR_COMPLETE: "Reviu pengendalian mutu selesai",
  FS_TIE_OUT: "Tie-out laporan keuangan",
  REPORTING_APPROVED: "Otorisasi penerbitan laporan",
  NA_REASONS_RECORDED: "Alasan tidak berlaku tercatat",
};

const TABS = [
  ["ringkasan", "Ringkasan", () => true],
  ["indeks", "Indeks A–E", () => true],
  ["kelengkapan", "Kelengkapan", (v) => !!v.completeness],
  ["gerbang", "Gerbang Mutu", (v) => !!v.quality_gates],
  ["rantai", "Rantai Bukti", (v) => (v.chains || []).length > 0],
  ["catatan", "Catatan Reviu", (v) => (v.review_notes || []).length > 0],
  ["jurnal", "AJE / RJE", (v) => (v.adjustments || []).length > 0],
  ["salah", "Kesalahan Penyajian", (v) => (v.misstatements || []).length > 0],
  ["bukti", "Indeks Bukti", (v) => (v.evidence_index || []).length > 0],
  ["finalisasi", "Finalisasi & Arsip", () => true],
  ["integrasi", "Integrasi WP", (v) => !!v.integration],
  ["antrean-ewp", "EWP Belum Dipetakan", (v) => (v.integration?.ewps?.pending || []).length > 0],
  ["antrean-bukti", "Evidence Belum Dipetakan",
    (v) => (v.integration?.evidence?.pending || []).length > 0],
  ["antrean-risiko", "Risk Belum Dipetakan",
    (v) => (v.integration?.risks?.pending || []).length > 0],
  ["antrean-prosedur", "Procedure Belum Dipetakan",
    (v) => (v.integration?.procedures?.pending || []).length > 0],
  ["alias", "Alias Legacy", () => true],
  ["riwayat", "Riwayat Mapping", () => true],
];

const MAPPING_LABELS = {
  EXACT_OBJECT_LINK: "Objek sumber persis",
  EXACT_LEGACY_ALIAS: "Alias indeks lama",
  EXACT_EWP_REF: "Referensi kanonik",
  MANUAL_AUDITOR_MAP: "Dipetakan auditor",
  UNMAPPED: "Belum terpetakan",
  PENDING_MAPPING: "Menunggu keputusan auditor",
};

const OBJECT_LABELS = { ewp: "Kertas kerja", evidence: "Bukti", risk: "Risiko",
  procedure: "Prosedur" };

const text = (value) => LABELS[value] || value || "—";

function Badge({ status, label }) {
  return (
    <span className={`ml-2 font-normal inline-block px-2 py-0.5 border rounded-sm ${STATUS_STYLE[status] || ""}`}>
      {label || text(status)}
    </span>
  );
}

function Figure({ label, value, status }) {
  return (
    <div className="border border-border rounded-sm p-2">
      <div className="text-muted-foreground">{label}</div>
      <div className={`font-medium tabular-nums ${status ? `inline-block mt-1 px-2 py-0.5 border rounded-sm ${STATUS_STYLE[status] || ""}` : ""}`}>
        {value ?? "—"}
      </div>
    </div>
  );
}

// Binder Audit — satu indeks atas berkas audit A–E. Isinya dibaca dari modul yang sudah memuat
// pekerjaan; binder tidak menyimpan kebenaran kedua dan tidak menyimpulkan apa pun sendiri.
export default function MasterBinder({ id }) {
  const [view, setView] = useState(null);
  const [tab, setTab] = useState("ringkasan");
  const [filters, setFilters] = useState({ section: "", cycle: "", status: "" });
  const [history, setHistory] = useState([]);
  const [draft, setDraft] = useState({});
  const [choice, setChoice] = useState({});

  const load = useCallback(() => {
    api.get(`/engagements/${id}/master-binder`).then(({ data }) => setView(data))
      .catch((e) => toast.error(getErrorMessage(e)));
    api.get(`/engagements/${id}/master-binder/mappings`).then(({ data }) => setHistory(data))
      .catch(() => setHistory([]));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const mapObject = (row, wpRef) => {
    const note = (draft[row.object_id] || "").trim();
    if (!wpRef) { toast.error("Pilih referensi kertas kerja kanonik terlebih dahulu."); return; }
    if (!note) { toast.error("Alasan pemetaan harus diisi."); return; }
    api.post(`/engagements/${id}/master-binder/mappings`, {
      object_type: row.object_type, object_id: row.object_id, wp_ref: wpRef, note,
    }).then(() => { toast.success(`Dipetakan ke ${wpRef}.`); load(); })
      .catch((e) => toast.error(getErrorMessage(e)));
  };

  const unmap = (mapping) => {
    const reason = (draft[mapping.id] || "").trim();
    if (!reason) { toast.error("Alasan pembatalan pemetaan harus diisi."); return; }
    api.delete(`/engagements/${id}/master-binder/mappings/${mapping.id}`,
      { params: { reason } })
      .then(() => { toast.success("Pemetaan dibatalkan."); load(); })
      .catch((e) => toast.error(getErrorMessage(e)));
  };

  const rows = useMemo(() => {
    if (!view) return [];
    return (view.items || []).filter((row) =>
      (!filters.section || row.section === filters.section)
      && (!filters.cycle || row.cycle === filters.cycle)
      && (!filters.status || row.status === filters.status));
  }, [view, filters]);

  const download = (path, name) =>
    downloadFile(`/engagements/${id}/master-binder/${path}`, name)
      .catch((e) => toast.error(getErrorMessage(e)));

  if (!view) {
    return <div className="text-xs text-muted-foreground p-4">Memuat binder audit…</div>;
  }
  const gates = view.quality_gates || { gates: [], failed: [] };
  const summary = view.completeness || {};
  const available = TABS.filter(([, , when]) => when(view));

  return (
    <div className="space-y-4" data-testid="master-binder">
      <div className="bg-white border border-border rounded-sm p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 font-head font-semibold text-sm mr-2">
          <FolderTree size={15} />Binder Audit
        </div>
        <div className="text-xs text-muted-foreground">{view.audit_period_display}</div>
        <span className="text-xs" data-testid="master-binder-status">
          Status<Badge status={view.binder_status} />
        </span>
        <span className="text-xs" data-testid="master-binder-package">
          Paket: <span className="font-mono">{view.package_name}</span>
        </span>
        <Button size="sm" variant="outline" className="ml-auto"
          onClick={() => download("index.xlsx", `binder-indeks-${id}.xlsx`)}
          data-testid="master-binder-export-index">
          <FileDown size={14} className="mr-1" />Indeks Binder
        </Button>
        <Button size="sm" variant="outline"
          onClick={() => download("package.zip", view.package_name)}
          data-testid="master-binder-export-package">
          <FileDown size={14} className="mr-1" />Unduh Paket
        </Button>
        <Button size="sm" variant="outline"
          onClick={() => download("binder.pdf", view.package_name.replace(/\.zip$/, "_FULL.pdf"))}
          data-testid="master-binder-export-pdf">
          <FileDown size={14} className="mr-1" />Binder PDF
        </Button>
      </div>

      {gates.failed?.length > 0 && (
        <div className="bg-white border border-amber-300 rounded-sm p-3 text-xs text-amber-900"
          data-testid="master-binder-not-final">
          Binder belum final. {gates.failed.length} gerbang mutu belum terpenuhi, sehingga paket
          yang diunduh bernama <span className="font-mono">{view.package_name}</span> dan tidak
          dinyatakan sebagai berkas audit final.
        </div>
      )}

      {view.staleness?.stale && (
        <div className="bg-white border border-red-300 rounded-sm p-3 text-xs text-red-900"
          data-testid="master-binder-stale">
          Sumber binder berubah setelah finalisasi. Binder ditandai usang dan tidak diperbarui
          secara diam-diam; penggantiannya adalah keputusan auditor.
        </div>
      )}

      <div className="bg-white border border-border rounded-sm">
        <div className="flex flex-wrap gap-1 border-b border-border p-2">
          {available.map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)}
              data-testid={`master-binder-tab-${key}`}
              className={`text-xs px-2 py-1 rounded-sm border ${tab === key
                ? "bg-slate-900 text-white border-slate-900" : "border-border"}`}>
              {label}
            </button>
          ))}
        </div>

        <div className="p-3">
          {tab === "ringkasan" && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs"
              data-testid="master-binder-summary">
              <Figure label="Kertas kerja terindeks" value={(view.items || []).length} />
              <Figure label="Kertas kerja wajib" value={summary.required} />
              <Figure label="Selesai" value={summary.completed} />
              <Figure label="Kelengkapan" value={`${summary.percentage ?? 0}%`} />
              <Figure label="Belum dimulai" value={summary.not_started} />
              <Figure label="Tertahan" value={summary.blocked} />
              <Figure label="Usang" value={summary.stale} />
              <Figure label="Belum dipetakan ke modul" value={summary.unmapped} />
              <Figure label="Catatan reviu terbuka" value={summary.open_review_notes} />
              <Figure label="Gerbang lulus" value={`${gates.passed ?? 0}/${gates.total ?? 0}`} />
              <Figure label="Materialitas" value={text(view.materiality_status)}
                status={view.materiality_status} />
              <Figure label="Arsip" value={view.archive?.locked ? "Terkunci" : "Belum dikunci"}
                status={view.archive?.locked ? "LOCKED" : "DRAFT"} />
            </div>
          )}

          {tab === "indeks" && (
            <div className="space-y-2 text-xs" data-testid="master-binder-index">
              <div className="flex flex-wrap gap-2">
                <select className="border border-border rounded-sm px-2 py-1"
                  data-testid="master-binder-filter-section" value={filters.section}
                  onChange={(e) => setFilters({ ...filters, section: e.target.value })}>
                  <option value="">Semua bagian</option>
                  {Object.entries(view.sections || {}).map(([letter, title]) => (
                    <option key={letter} value={letter}>{letter} — {title}</option>
                  ))}
                </select>
                <select className="border border-border rounded-sm px-2 py-1"
                  data-testid="master-binder-filter-cycle" value={filters.cycle}
                  onChange={(e) => setFilters({ ...filters, cycle: e.target.value })}>
                  <option value="">Semua siklus</option>
                  {Object.entries(view.cycles || {}).map(([prefix, title]) => (
                    <option key={prefix} value={prefix}>{prefix} {title}</option>
                  ))}
                </select>
                <select className="border border-border rounded-sm px-2 py-1"
                  data-testid="master-binder-filter-status" value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
                  <option value="">Semua status</option>
                  {(view.statuses || []).map((status) => (
                    <option key={status} value={status}>{text(status)}</option>
                  ))}
                </select>
                <span className="self-center text-muted-foreground"
                  data-testid="master-binder-index-count">{rows.length} kertas kerja</span>
              </div>
              <table className="w-full audit-table text-xs">
                <thead><tr>
                  <th>Ref</th><th>Judul</th><th>SA</th><th>Asersi</th><th>Status</th>
                  <th className="text-right">Bukti</th><th>Penyusun</th><th>Pereviu</th>
                  <th>Modul</th>
                </tr></thead>
                <tbody>{rows.map((row) => (
                  <tr key={row.wp_ref} data-testid={`master-binder-row-${row.wp_ref}`}>
                    <td className="font-mono">{row.wp_ref}</td>
                    <td>{row.title}</td>
                    <td>{(row.sa_references || []).join(", ") || "—"}</td>
                    <td>{(row.assertions || []).join(", ") || "—"}</td>
                    <td><Badge status={row.status} /></td>
                    <td className="text-right tabular-nums">{row.evidence_count}</td>
                    <td>{row.prepared_by || "—"}</td>
                    <td>{row.reviewed_by || "—"}</td>
                    <td>{(row.source_modules || []).join(", ")
                      || <span className="text-muted-foreground">Belum dipetakan</span>}</td>
                  </tr>))}
                </tbody>
              </table>
              <div className="text-muted-foreground">{view.basis}</div>
            </div>
          )}

          {tab === "kelengkapan" && (
            <div className="space-y-2 text-xs" data-testid="master-binder-completeness">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <Figure label="Kertas kerja wajib" value={summary.required} />
                <Figure label="Selesai" value={summary.completed} />
                <Figure label="Tidak berlaku beralasan"
                  value={summary.not_applicable_with_reason} />
                <Figure label="Tidak berlaku tanpa alasan"
                  value={summary.not_applicable_without_reason} />
                <Figure label="Tanpa simpulan" value={summary.missing_conclusion} />
                <Figure label="Tanpa pereviu" value={summary.missing_reviewer} />
                <Figure label="Tanpa bukti" value={summary.missing_evidence} />
                <Figure label="Kelengkapan" value={`${summary.percentage ?? 0}%`} />
              </div>
              <div className="text-muted-foreground">{summary.basis}</div>
            </div>
          )}

          {tab === "gerbang" && (
            <div className="space-y-2 text-xs" data-testid="master-binder-gates">
              <table className="w-full audit-table text-xs">
                <thead><tr><th>Gerbang</th><th>Modul</th><th>Status</th><th>Alasan</th></tr></thead>
                <tbody>{(gates.gates || []).map((gate) => (
                  <tr key={gate.code} data-testid={`master-binder-gate-${gate.code}`}>
                    <td>{GATE_LABELS[gate.code] || gate.code}</td>
                    <td>{gate.module}</td>
                    <td><Badge status={gate.ok ? "COMPLETED" : "BLOCKED"}
                      label={gate.ok ? "Lulus" : "Belum"} /></td>
                    <td>{gate.reason || "—"}</td>
                  </tr>))}
                </tbody>
              </table>
              <div className="text-muted-foreground">{gates.basis}</div>
            </div>
          )}

          {tab === "rantai" && (
            <div className="space-y-2 text-xs" data-testid="master-binder-chains">
              <table className="w-full audit-table text-xs">
                <thead><tr>
                  <th>Ref</th><th>Risiko</th><th>Asersi</th><th>Respons</th><th>Prosedur</th>
                  <th>Populasi</th><th>Sampel</th><th>Bukti</th><th>Simpulan</th>
                </tr></thead>
                <tbody>{(view.chains || []).slice(0, 400).map((link) => (
                  <tr key={link.wp_ref}>
                    <td className="font-mono">{link.wp_ref}</td>
                    <td><Badge status={link.risk.state} /></td>
                    <td><Badge status={link.assertions.state} /></td>
                    <td><Badge status={link.planned_response.state} /></td>
                    <td><Badge status={link.procedure.state} /></td>
                    <td><Badge status={link.population.state} /></td>
                    <td><Badge status={link.sample.state} /></td>
                    <td><Badge status={link.evidence.state} /></td>
                    <td><Badge status={link.conclusion.state} /></td>
                  </tr>))}
                </tbody>
              </table>
            </div>
          )}

          {tab === "catatan" && (
            <table className="w-full audit-table text-xs" data-testid="master-binder-notes">
              <thead><tr>
                <th>Ref KKA</th><th>Pereviu</th><th>Catatan</th><th>Status</th><th>Resolusi</th>
              </tr></thead>
              <tbody>{(view.review_notes || []).map((note) => (
                <tr key={note.id}>
                  <td className="font-mono">{note.wp_ref || "—"}</td>
                  <td>{note.reviewer || "—"}</td>
                  <td>{note.note || "—"}</td>
                  <td>{note.status || "—"}</td>
                  <td>{note.resolution || "—"}</td>
                </tr>))}
              </tbody>
            </table>
          )}

          {tab === "jurnal" && (
            <table className="w-full audit-table text-xs" data-testid="master-binder-adjustments">
              <thead><tr>
                <th>ID</th><th>Jenis</th><th>Ref KKA</th><th>Uraian</th><th>Status</th>
              </tr></thead>
              <tbody>{(view.adjustments || []).map((entry) => (
                <tr key={entry.id}>
                  <td className="font-mono">{entry.id}</td><td>{entry.kind}</td>
                  <td className="font-mono">{entry.wp_ref || "—"}</td>
                  <td>{entry.description || "—"}</td><td>{entry.status || "—"}</td>
                </tr>))}
              </tbody>
            </table>
          )}

          {tab === "salah" && (
            <table className="w-full audit-table text-xs" data-testid="master-binder-misstatements">
              <thead><tr>
                <th>ID</th><th>Ref KKA</th><th>Uraian</th><th>Jenis</th><th>Disposisi</th>
              </tr></thead>
              <tbody>{(view.misstatements || []).map((entry) => (
                <tr key={entry.id}>
                  <td className="font-mono">{entry.id}</td>
                  <td className="font-mono">{entry.wp_ref || "—"}</td>
                  <td>{entry.description || "—"}</td><td>{entry.kind || "—"}</td>
                  <td>{entry.disposition
                    || <span className="text-amber-800">Belum didisposisi</span>}</td>
                </tr>))}
              </tbody>
            </table>
          )}

          {tab === "bukti" && (
            <table className="w-full audit-table text-xs" data-testid="master-binder-evidence">
              <thead><tr>
                <th>Ref KKA</th><th>Berkas</th><th>Jenis</th><th>Sidik jari</th><th>Diperoleh</th>
              </tr></thead>
              <tbody>{(view.evidence_index || []).slice(0, 500).map((entry) => (
                <tr key={entry.id}>
                  <td className="font-mono">{entry.wp_ref || "—"}</td>
                  <td>{entry.filename || "—"}</td><td>{entry.source_type || "—"}</td>
                  <td className="font-mono">{(entry.sha256 || "").slice(0, 16) || "—"}</td>
                  <td>{entry.obtained_at || "—"}</td>
                </tr>))}
              </tbody>
            </table>
          )}

          {tab === "integrasi" && (
            <div className="space-y-2 text-xs" data-testid="master-binder-integration">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <Figure label="Kertas kerja tertaut"
                  value={view.integration.summary.ewps_linked} />
                <Figure label="Kertas kerja menunggu"
                  value={view.integration.summary.ewps_pending} />
                <Figure label="Bukti tertaut" value={view.integration.summary.evidence_linked} />
                <Figure label="Bukti menunggu" value={view.integration.summary.evidence_pending} />
                <Figure label="Risiko tertaut" value={view.integration.summary.risks_linked} />
                <Figure label="Risiko menunggu" value={view.integration.summary.risks_pending} />
                <Figure label="Prosedur tertaut"
                  value={view.integration.summary.procedures_linked} />
                <Figure label="Prosedur menunggu"
                  value={view.integration.summary.procedures_pending} />
                <Figure label="Referensi dengan objek sumber"
                  value={view.integration.summary.references_with_a_source_object} />
                <Figure label="Referensi belum terpetakan"
                  value={view.integration.summary.references_unmapped} />
                <Figure label="Objek sumber tertaut"
                  value={view.integration.summary.source_objects} />
                <Figure label="Antrean pemetaan" value={(view.integration.queue || []).length} />
              </div>
              <div className="text-muted-foreground">{view.integration.basis}</div>
            </div>
          )}

          {["antrean-ewp", "antrean-bukti", "antrean-risiko", "antrean-prosedur"]
            .includes(tab) && (
            <Queue
              rows={(view.integration[{
                "antrean-ewp": "ewps", "antrean-bukti": "evidence",
                "antrean-risiko": "risks", "antrean-prosedur": "procedures",
              }[tab]] || {}).pending || []}
              refs={(view.items || []).map((r) => r.wp_ref)}
              draft={draft} setDraft={setDraft} choice={choice} setChoice={setChoice}
              onMap={mapObject} testid={`master-binder-queue-${tab}`} />
          )}

          {tab === "alias" && (
            <div className="space-y-3 text-xs" data-testid="master-binder-alias">
              <div>
                <div className="font-head font-semibold pb-1">
                  Alias pasti — dipetakan otomatis
                </div>
                <table className="w-full audit-table text-xs">
                  <thead><tr><th>Kode indeks lama</th><th>Ref kanonik</th></tr></thead>
                  <tbody>{Object.entries(view.integration.legacy_aliases || {}).map(
                    ([legacy, canonical]) => (
                      <tr key={legacy}>
                        <td className="font-mono">{legacy}</td>
                        <td className="font-mono">{canonical}</td>
                      </tr>))}
                  </tbody>
                </table>
              </div>
              <div>
                <div className="font-head font-semibold pb-1">
                  Alias bermakna ganda — diputuskan auditor
                </div>
                <table className="w-full audit-table text-xs">
                  <thead><tr><th>Kode indeks lama</th><th>Kandidat</th></tr></thead>
                  <tbody>{Object.entries(view.integration.ambiguous_legacy || {}).map(
                    ([legacy, candidates]) => (
                      <tr key={legacy}>
                        <td className="font-mono">{legacy}</td>
                        <td className="font-mono">{(candidates || []).join(", ")}</td>
                      </tr>))}
                  </tbody>
                </table>
              </div>
              <div className="text-muted-foreground">
                Hanya alias yang menunjuk tepat satu kertas kerja kanonik dipetakan otomatis.
                Referensi lama tidak pernah ditimpa.
              </div>
            </div>
          )}

          {tab === "riwayat" && (
            <div className="space-y-2 text-xs" data-testid="master-binder-mapping-history">
              <table className="w-full audit-table text-xs">
                <thead><tr>
                  <th>Objek</th><th>ID</th><th>Ref kanonik</th><th>Alasan</th><th>Oleh</th>
                  <th>Tanggal</th><th>Status</th><th />
                </tr></thead>
                <tbody>{(history || []).map((mapping) => (
                  <tr key={mapping.id} data-testid={`master-binder-mapping-${mapping.id}`}>
                    <td>{OBJECT_LABELS[mapping.object_type] || mapping.object_type}</td>
                    <td className="font-mono">{mapping.object_id}</td>
                    <td className="font-mono">{mapping.wp_ref}</td>
                    <td>{mapping.note}</td>
                    <td>{mapping.mapped_by}</td>
                    <td>{mapping.mapped_at}</td>
                    <td>{mapping.removed_at
                      ? <span className="text-muted-foreground">
                          Dibatalkan — {mapping.removed_reason}
                        </span>
                      : <Badge status="LINKED" label="Berlaku" />}</td>
                    <td>{!mapping.removed_at && (
                      <div className="flex gap-1">
                        <input className="border border-border rounded-sm px-1 py-0.5 w-40"
                          placeholder="Alasan pembatalan"
                          value={draft[mapping.id] || ""}
                          onChange={(e) => setDraft({ ...draft, [mapping.id]: e.target.value })} />
                        <Button size="sm" variant="outline" onClick={() => unmap(mapping)}
                          data-testid={`master-binder-unmap-${mapping.id}`}>
                          Batalkan Mapping
                        </Button>
                      </div>)}</td>
                  </tr>))}
                </tbody>
              </table>
              {!(history || []).length && (
                <div className="text-muted-foreground">Belum ada pemetaan manual.</div>
              )}
            </div>
          )}

          {tab === "finalisasi" && (
            <div className="space-y-2 text-xs" data-testid="master-binder-finalisation">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <Figure label="Status binder" value={text(view.binder_status)}
                  status={view.binder_status} />
                <Figure label="Nama paket" value={view.package_name} />
                <Figure label="Gerbang gagal" value={gates.failed?.length ?? 0} />
                <Figure label="Versi" value={view.manifest?.version ?? "—"} />
                <Figure label="Difinalisasi oleh" value={view.manifest?.finalised_by ?? "—"} />
                <Figure label="Dikunci oleh" value={view.archive?.locked_by ?? "—"} />
              </div>
              {gates.failed?.length > 0 ? (
                <div className="text-amber-900" data-testid="master-binder-finalise-blocked">
                  Finalisasi belum dapat dilakukan. Gerbang yang belum terpenuhi:{" "}
                  {gates.failed.map((code) => GATE_LABELS[code] || code).join(", ")}. Tidak ada
                  gerbang yang dapat dilewati oleh ekspor.
                </div>
              ) : (
                <div className="flex items-center gap-2 text-green-900"
                  data-testid="master-binder-finalise-ready">
                  <Lock size={14} />
                  Seluruh gerbang terpenuhi. Finalisasi tetap merupakan keputusan rekan perikatan.
                </div>
              )}
              <div className="text-muted-foreground">{gates.basis}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Queue({ rows, refs, draft, setDraft, choice, setChoice, onMap, testid }) {
  return (
    <div className="space-y-2 text-xs" data-testid={testid}>
      <table className="w-full audit-table text-xs">
        <thead><tr>
          <th>Sumber</th><th>Ref saat ini</th><th>Kandidat</th><th>Alasan belum terpetakan</th>
          <th>Ref kanonik</th><th>Alasan pemetaan</th><th />
        </tr></thead>
        <tbody>{rows.map((row) => (
          <tr key={row.object_id} data-testid={`queue-row-${row.object_id}`}>
            <td>
              {OBJECT_LABELS[row.object_type] || row.object_type}
              <div className="font-mono text-muted-foreground">{row.object_id}</div>
            </td>
            <td className="font-mono">{row.legacy_ref || "—"}</td>
            <td className="font-mono">{(row.candidates || []).join(", ") || "—"}</td>
            <td>{row.reason}</td>
            <td>
              <select className="border border-border rounded-sm px-1 py-0.5"
                data-testid={`queue-ref-${row.object_id}`}
                value={choice[row.object_id] || ""}
                onChange={(e) => setChoice({ ...choice, [row.object_id]: e.target.value })}>
                <option value="">Pilih…</option>
                {(row.candidates && row.candidates.length ? row.candidates : refs).map((ref) => (
                  <option key={ref} value={ref}>{ref}</option>
                ))}
              </select>
            </td>
            <td>
              <input className="border border-border rounded-sm px-1 py-0.5 w-44"
                data-testid={`queue-note-${row.object_id}`}
                placeholder="Alasan pemetaan"
                value={draft[row.object_id] || ""}
                onChange={(e) => setDraft({ ...draft, [row.object_id]: e.target.value })} />
            </td>
            <td>
              <Button size="sm" variant="outline"
                data-testid={`queue-map-${row.object_id}`}
                onClick={() => onMap(row, choice[row.object_id])}>Petakan</Button>
            </td>
          </tr>))}
        </tbody>
      </table>
      {!rows.length && (
        <div className="text-muted-foreground">Tidak ada objek yang menunggu pemetaan.</div>
      )}
      <div className="text-muted-foreground">
        Nama berkas tidak pernah dijadikan dasar penautan. Objek yang maknanya ganda menunggu
        keputusan auditor, dan setiap keputusan dicatat.
      </div>
    </div>
  );
}
